<script setup lang="ts">
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import { BreadcrumbRoute } from "~/types/breadcrumb-routes";

// --- Content interfaces ---
interface MetaTag {
  hid?: string;
  name?: string;
  content: string;
}

interface MetaTags {
  title: string;
  meta: MetaTag[];
}

interface HeroStruggle {
  label: string;
  svgIcon: string;
}

interface HeroSection {
  heading: string;
  subcopy: string;
  ctaText: string;
  imageSrc: string;
  lightImageSrc: string;
  width: string;
  height: string;
}

interface StrugglesSection {
  heading: string;
  subcopy: string;
  struggles: HeroStruggle[];
  ctaText: string;
}

interface CapabilityCard {
  svgIcon: string;
  title: string;
  desc: string;
}

interface CapabilityCardsSection {
  headingLine1: string;
  headingLine2: string;
  subcopy: string;
  cards: CapabilityCard[];
  ctaText: string;
}

interface ProblemSection {
  heading: string;
  intro: string;
  problemLabel: string;
  problems: string[];
  solutionLabel: string;
  solutions: string[];
  quote: string;
  ctaText: string;
}

interface DualCardItem {
  svgIcon: string;
  title: string;
  description: string;
}

interface DualCardsSection {
  heading: string;
  subtitle: string;
  groupOneTitle: string;
  groupOneCards: DualCardItem[];
  groupTwoTitle: string;
  groupTwoCards: DualCardItem[];
  ctaText?: string;
}

interface ServiceContentSection {
  heading: string;
  subheading: string;
  text: string[];
}

interface FeatureCard {
  number: string;
  title: string;
  description: string;
  svgIcon?: string;
}

interface FeaturesSection {
  heading: string;
  subcopy: string;
  svgIcons: string[];
  cards: FeatureCard[];
  ctaText: string;
}

interface ProcessStep {
  id: string;
  title: string;
  description: string;
}

interface ProcessStepsSection {
  heading: string;
  subcopy: string;
  steps: ProcessStep[];
  ctaText: string;
}

interface BenefitCard {
  svgIcon: string;
  title: string;
  description: string;
}

interface BenefitCardsSection {
  heading: string;
  subcopy: string;
  cards: BenefitCard[];
}

interface PromiseCard {
  svgIcon: string;
  title: string;
  description: string;
}

interface OurPromisesSection {
  heading: string;
  subcopy: string;
  cards: PromiseCard[];
}

interface WhyChooseItem {
  title: string;
  description: string;
}

interface OverlayPosition {
  top: string;
  left: string;
}

interface WhyChooseSection {
  heading: string;
  subcopy: string;
  items: WhyChooseItem[];
  overlayPositions: OverlayPosition[];
}

interface FaqItem {
  title: string;
  description: string;
  tags: string[];
}

interface FaqSection {
  heading: string;
  suffix: string;
  subtext: string;
  filterTags: string[];
  ctaText: string;
  items: FaqItem[];
}

interface ShopifyContent {
  meta_tags: MetaTags;
  hero: HeroSection;
  struggles: StrugglesSection;
  capabilityCards: CapabilityCardsSection;
  problem: ProblemSection;
  dualCards: DualCardsSection;
  service_content: ServiceContentSection;
  features: FeaturesSection;
  processSteps: ProcessStepsSection;
  benefitCards: BenefitCardsSection;
  ourPromises: OurPromisesSection;
  whyChoose: WhyChooseSection;
  faq: FaqSection;
}

// --- Fetch content ---
const url = useRequestURL();
const { data, error } = await useFetch(
  `${url.origin}/content/services/cms-development/shopify-development-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

const content = data.value as ShopifyContent;
const {
  meta_tags,
  hero,
  struggles,
  capabilityCards,
  problem,
  dualCards,
  service_content,
  features,
  processSteps,
  benefitCards,
  ourPromises,
  whyChoose,
  faq,
} = content;

definePageMeta({
  layout: "services-layout",
});

const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Services);

useHead({
  title: meta_tags.title,
  meta: [
    ...meta_tags.meta.map((m: MetaTag) => ({ name: m.name, content: m.content, hid: m.hid })),
    { name: "robots", content: "index, follow" },
    { property: "og:title", content: meta_tags.title },
    {
      property: "og:description",
      content:
        meta_tags.meta.find((m: MetaTag) => m.name === "description")?.content ||
        hero.subcopy ||
        "",
    },
  ],
  link: [
    {
      rel: "preload",
      as: "image",
      href: hero.imageSrc,
    },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: hero.heading,
            serviceType: hero.heading,
            description: hero.subcopy,
            provider: {
              "@type": "Organization",
              name: "WebReinvent",
              url: "https://webreinvent.com",
            },
            url: `https://webreinvent.com${useRoute().path}`,
          },
          {
            "@type": "FAQPage",
            mainEntity: faq.items.map((item: FaqItem) => ({
              "@type": "Question",
              name: item.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.description,
              },
            })),
          },
          ...(breadcrumbs.value ? [breadcrumbs.value] : []),
        ],
      }),
    },
  ],
});

const visible = ref(false);

const openModal = () => {
  visible.value = true;
};

// --- Component imports ---
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";

const WrDesignStrugglesSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignStrugglesSection.vue"),
);
const WrDesignServiceCardsSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignServiceCardsSection.vue"),
);
const WrDifferenceSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDifferenceSection.vue"),
);
const WrDesignDualCardsSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignDualCardsSection.vue"),
);
const WrWhoAreWeCard = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrWhoAreWeCard.vue"),
);
const WrDesignIconLineDecorativeSquaresCards = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignIconLineDecorativeSquaresCards.vue"),
);
const WrDesignReadyToElevateYourBrand = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignReadyToElevateYourBrand.vue"),
);
const WrDesignBenefitCards = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignBenefitCards.vue"),
);
const WrDesignWhyWeAreDifferent = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignWhyWeAreDifferent.vue"),
);
const WrDesignFaqsSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignFaqsSection.vue"),
);
const WrDesignLevelUpYourCreativityWithAi = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignLevelUpYourCreativityWithAi.vue"),
);
const WrOurServiceSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrOurServiceSection.vue"),
);
const WrSelectedProject = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrSelectedProject.vue"),
);
const WrContactFormPopup = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrContactFormPopup.vue"),
);

// --- Computed mappings ---
const problemLeftList = computed(() => ({
  title: problem.problemLabel || "",
  items: problem.problems || [],
}));

const problemRightList = computed(() => ({
  title: problem.solutionLabel || "",
  items: problem.solutions || [],
}));
</script>

<template>
  <!-- ==================== HERO SECTION ==================== -->
  <div class="max-w-screen-xl px-4 mx-auto pt-32">
    <WrDesignHeroCard
      :heading="hero.heading"
      :subcopy="hero.subcopy"
      :ctaText="hero.ctaText"
      :imageSrc="hero.imageSrc"
      :lightImageSrc="hero.lightImageSrc"
      :width="hero.width"
      :height="hero.height"
      @cta-clicked="openModal"
    />
  </div>

  <!-- ==================== STRUGGLES SECTION ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignStrugglesSection
      :heading="struggles.heading"
      :subcopy="struggles.subcopy"
      :struggles="struggles.struggles"
      :ctaText="struggles.ctaText"
      bgPosition="50% -240px"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== CAPABILITY CARDS (WrDesignServiceCardsSection) ==================== -->
  <section class="max-w-[1080px] px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignServiceCardsSection
      :headingLine1="capabilityCards.headingLine1"
      :headingLine2="capabilityCards.headingLine2"
      :subcopy="capabilityCards.subcopy"
      :cards="capabilityCards.cards"
      :ctaText="capabilityCards.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== PROBLEM / SOLUTION ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDifferenceSection
      :heading="problem.heading"
      :description="problem.intro"
      :leftList="problemLeftList"
      :rightList="problemRightList"
      :subcopyClass="'max-w-[900px]'"
      :quote="problem.quote"
      :ctaText="problem.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== DUAL CARDS (WrDesignDualCardsSection) ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignDualCardsSection
      :heading="dualCards.heading"
      :subtitle="dualCards.subtitle"
      :groupOneTitle="dualCards.groupOneTitle"
      :groupOneCards="dualCards.groupOneCards"
      :groupTwoTitle="dualCards.groupTwoTitle"
      :groupTwoCards="dualCards.groupTwoCards"
      :ctaText="dualCards.ctaText || undefined"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== SERVICE CONTENT ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto my-20 relative z-10">
    <WrWhoAreWeCard
      :heading="service_content.heading"
      :subheading="service_content.subheading"
      :content="service_content.text"
    />
  </section>

  <!-- ==================== FEATURES (WrDesignIconLineDecorativeSquaresCards) ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignIconLineDecorativeSquaresCards
      :heading="features.heading"
      :subcopy="features.subcopy"
      :cards="features.cards"
      :ctaText="features.ctaText"
      @cta-clicked="openModal"
    >
      <template v-for="(card, i) in features.cards" :key="i" #[`icon-${i}`]>
        <WrIcon
          :name="features.svgIcons?.[i] || card.svgIcon || ''"
          class="size-8 text-[#613FAA] dark:text-white"
        />
      </template>
    </WrDesignIconLineDecorativeSquaresCards>
  </section>

  <!-- ==================== PROCESS STEPS ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignReadyToElevateYourBrand
      :heading="processSteps.heading"
      :subcopy="processSteps.subcopy"
      :cards="processSteps.steps"
      :ctaText="processSteps.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== BENEFITS (WrDesignBenefitCards) ==================== -->
  <div class="max-w-screen-xl px-4 mx-auto">
    <WrDesignBenefitCards
      :heading="benefitCards.heading"
      :subcopy="benefitCards.subcopy"
      :cards="benefitCards.cards"
    />
  </div>

  <!-- ==================== OUR PROMISES ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignLevelUpYourCreativityWithAi
      :heading="ourPromises.heading"
      :subcopy="ourPromises.subcopy"
      :cards="ourPromises.cards"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== WHY CHOOSE US (WrDesignWhyWeAreDifferent) ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignWhyWeAreDifferent
      :heading="whyChoose.heading"
      :subcopy="whyChoose.subcopy"
      :items="whyChoose.items"
      :overlayPositions="whyChoose.overlayPositions"
    />
  </section>

  <!-- ==================== FAQ SECTION ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignFaqsSection
      :heading="faq.heading"
      :suffix="faq.suffix"
      :subtext="faq.subtext"
      :filterTags="faq.filterTags"
      :faqItems="faq.items"
      :ctaText="faq.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== BOTTOM SECTIONS ==================== -->
  <section
    class="relative z-10 max-w-screen-xl lg:-mt-44 lg:pb-28 overflow-visible grid place-items-center order-9"
  >
    <div class="max-w-screen-lg mx-auto px-4 w-full relative">
      <div
        class="hidden dark:block opacity-30 size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
      ></div>
      <div
        class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[image:var(--bg-circle-top-light)] absolute -bottom-[15%] -right-80"
      ></div>
    </div>
  </section>

  <section class="section-spacing">
    <div class="container-wrapper-lg">
      <WrSelectedProject />
    </div>
  </section>

  <WrOurServiceSection
    title="Our Capabilities"
    description="One of our latest add-on skills from the newest technologies."
  />

  <section
    class="relative section-spacing dark:bg-none overflow-hidden z-20"
  >
    <div class="container-wrapper-xl">
      <h4 class="text-center mb-11">
        <WrGradientText
          text="Trusted for excellence and efficiency"
          append-class="text-3xl lg:text-[48px]"
        />
        <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
          WebReinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h4>
      <WrTestimonialCardCarousel class="mb-12" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </div>
  </section>

  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />
</template>
<style scoped>
:deep(.hero-image-wrapper){
    max-width: 500px;
}
</style>
